import { useMutation, useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { CompanyEventWebhookStatusEnumType } from '__generated__/graphql';
import { FC, useEffect, useState } from 'react';

import Button from 'primitives/Button';
import ConfirmButton from 'primitives/ConfirmButton';
import LoadingIndicator from 'primitives/LoadingIndicator';

import DataTable from 'components/DataTable';

import CreateCompanyEventFromWebhookDialogButton from '../Company/Events/CreateEventFromWebhookDialogButton';

const WEBHOOKS_QUERY = gql(`
  query fetchCompanyEventWebhooks($cursor: ID, $limit: Int, $filters: CompanyEventWebhooksFilterType) {
    companyEventWebhooks(cursor: $cursor, limit: $limit, filters: $filters) {
      nodes {
        id
        messageId
        senderId
        status
        createdAt
        updatedAt
      }
      pageInfo {
        hasNextPage
        cursor
      }
    }
  }
`);

const INITIATE_GOOGLE_AUTH = gql(`
  mutation initiateGoogleAuth {
    initiateGoogleAuth
  }
`);

const AUTHENTICATE_GOOGLE = gql(`
  mutation authenticateGoogle($code: String!) {
    authenticateGoogle(code: $code)
  }
`);

const CHECK_GOOGLE_AUTH = gql(`
  query checkGoogleAuth {
    isGoogleAuthValid
  }
`);

const REMOVE_COMPANY_EVENT_WEBHOOK_MUTATION = gql(`
  mutation RemoveCompanyEventWebhook($id: ID!) {
    removeEventWebhook(id: $id) {
      id
    }
  }
`);

const ApproveEventsPage: FC = () => {
  const [authLoading, setAuthLoading] = useState(false);
  const [, setAuthError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<{
    id: string;
    messageId: string;
    senderId: string;
  } | null>(null);

  const [initiateGoogleAuth] = useMutation(INITIATE_GOOGLE_AUTH);
  const [authenticateGoogle] = useMutation(AUTHENTICATE_GOOGLE);
  const [removeCompanyEventWebhook, { loading: removeLoading }] = useMutation(
    REMOVE_COMPANY_EVENT_WEBHOOK_MUTATION,
    {
      refetchQueries: ['fetchCompanyEventWebhooks'],
    }
  );

  const { loading: authCheckLoading, refetch: refetchAuthStatus } = useQuery(CHECK_GOOGLE_AUTH, {
    fetchPolicy: 'network-only',
    onCompleted: data => {
      setIsAuthenticated(data.isGoogleAuthValid);
    },
    onError: () => {
      setIsAuthenticated(false);
    },
  });

  const { loading, data, refetch, fetchMore } = useQuery(WEBHOOKS_QUERY, {
    variables: {
      limit: 50,
      filters: {
        status: CompanyEventWebhookStatusEnumType.Unassigned,
      },
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const handleGoogleAuth = async () => {
    try {
      setAuthLoading(true);
      setAuthError(null);

      const result = await initiateGoogleAuth();

      if (!result.data?.initiateGoogleAuth) {
        throw new Error('Failed to get authentication URL');
      }

      const authWindow = window.open(
        result.data.initiateGoogleAuth,
        '_blank',
        'width=800,height=600'
      );

      const messageHandler = async event => {
        if (event.data && event.data.type === 'GOOGLE_AUTH_CODE') {
          const code = event.data.code;

          try {
            await authenticateGoogle({ variables: { code } });
            if (authWindow) authWindow.close();
            setIsAuthenticated(true);
            refetch();
            refetchAuthStatus();
            window.removeEventListener('message', messageHandler);
          } catch (error: any) {
            setAuthError('Failed to authenticate with Google: ' + error.message);
            setIsAuthenticated(false);
          } finally {
            setAuthLoading(false);
          }
        }
      };

      window.addEventListener('message', messageHandler);
    } catch (error: any) {
      setAuthError('Failed to initiate Google authentication: ' + error.message);
      setAuthLoading(false);
    }
  };

  useEffect(() => {
    refetchAuthStatus();
  }, [refetchAuthStatus]);

  if (authCheckLoading) return <LoadingIndicator />;

  if (loading && !data) return <LoadingIndicator />;

  const events = data?.companyEventWebhooks.nodes || [];
  const pageInfo = data?.companyEventWebhooks.pageInfo || { hasNextPage: false };

  return (
    <div className="space-y-4 mt-4">
      <CreateCompanyEventFromWebhookDialogButton
        open={!!selectedEvent}
        onClose={() => setSelectedEvent(null)}
        messageId={selectedEvent?.messageId}
        webhookId={selectedEvent?.id}
        senderId={selectedEvent?.senderId || ''}
      />

      <div className="flex justify-between items-center">
        <h1 className="text-xl font-semibold">Approve Events</h1>
        <Button
          onClick={handleGoogleAuth}
          disabled={authLoading || isAuthenticated === true}
          loading={authLoading}
          variant={isAuthenticated ? 'secondary' : 'primary'}
        >
          {isAuthenticated ? 'Gmail Connected' : 'Connect Gmail'}
        </Button>
      </div>

      <DataTable
        data={events}
        searchFields={['messageId']}
        onClick={event => {
          setSelectedEvent({
            id: event.id,
            messageId: event.messageId,
            senderId: event.senderId,
          });
        }}
        columns={[
          {
            label: 'Message ID',
            fieldName: 'messageId',
          },
          {
            label: 'Sender',
            fieldName: 'senderId',
          },
          {
            label: 'Status',
            fieldName: 'status',
          },
          {
            label: 'Created At',
            fieldName: 'createdAt',
          },
          {
            label: 'Updated At',
            fieldName: 'updatedAt',
          },
          {
            label: 'Actions',
            fieldName: 'id',
            type: 'CUSTOM',
            customValue: id => {
              return (
                <ConfirmButton
                  variant="secondary"
                  className="text-red-900 hover:text-red-700"
                  onConfirm={() => removeCompanyEventWebhook({ variables: { id } })}
                  title="Delete Webhook"
                  loading={removeLoading}
                  description="Are you sure you want to delete this webhook?"
                >
                  Delete
                </ConfirmButton>
              );
            },
          },
        ]}
        hasNextPage={pageInfo.hasNextPage}
        paginationLoading={loading}
        onLoadMore={() =>
          fetchMore({
            variables: {
              cursor: pageInfo.cursor,
            },
          })
        }
      />
    </div>
  );
};

export default ApproveEventsPage;
